import React, { useState } from 'react';
import { 
  Paper, Box, Chip, Typography, Button,
  Dialog, DialogContent, DialogTitle, DialogActions
} from '@mui/material';
import {
  PlayCircle as PlayCircleIcon,
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import ShareDialog from './ShareDialog';
import { useFavorites } from '../context/FavoritesContext';
// 导入认证 store
import { useAuthStore } from '../stores/authStore';

const DramaCard = ({ drama }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  
  const { isFavorited, toggleFavorite, favorites } = useFavorites();
  // 状态管理分享弹窗
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  // 登录提示弹窗状态
  const [isLoginRequiredDialogOpen, setIsLoginRequiredDialogOpen] = useState(false);
  
  // 获取当前登录用户（支持双系统）
  const { mongoUser } = useAuthStore();
  
  // 检查是否已登录
  const checkIsLoggedIn = () => {
    // MongoDB 用户登录
    if (mongoUser && mongoUser._id) {
      return true;
    }
    // 旧版 JSON 用户登录
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user && user.id) {
        return true;
      }
    }
    return false;
  };

  // 处理剧集点击事件
  const handleDramaClick = async () => {
    console.log('剧集点击事件:', drama.id);
    
    // 调用 API 增加全局观看次数
    try {
      const response = await fetch(`/api/dramas/${drama.id}/views`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('观看次数更新成功:', data.viewCount);
          
          // 更新本地状态中的观看次数
          drama.views = data.viewCount;
          drama.viewCount = data.viewCount;
        } else {
          console.error('更新观看次数失败:', data.error);
        }
      } else {
        console.error('更新观看次数失败');
      }
    } catch (error) {
      console.error('更新观看次数时发生错误:', error);
    }
    
    history.push(`/drama/${drama.id}`);
  };

  // 处理收藏点击
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    console.log('收藏点击事件:', drama.id);
    console.log('当前剧集信息:', drama);
    
    // 检查登录状态
    if (!checkIsLoggedIn()) {
      setIsLoginRequiredDialogOpen(true);
      return;
    }
    
    // 使用全局状态管理切换收藏状态
    toggleFavorite(drama);
  };

  // 处理分享点击
  const handleShareClick = (e) => {
    e.stopPropagation();
    console.log('分享点击事件:', drama.id);
    setIsShareDialogOpen(true);
  };

  // 处理分享弹窗关闭
  const handleCloseShareDialog = () => {
    setIsShareDialogOpen(false);
  };

  // 处理评论点击
  const handleCommentClick = (e) => {
    e.stopPropagation();
    console.log('评论点击事件:', drama.id);
    // 这里可以添加评论逻辑
  };

  const isFavoritedState = isFavorited(drama.id);
  // 计算全局收藏次数 - 使用API返回的数据
  const favoriteCount = drama.favoriteCount || drama.likeCount || 0;

  return (
    <React.Fragment>
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: 1,
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
        onClick={handleDramaClick}
        id={`drama-card-${drama.id}`}
      >
        <Box sx={{ position: 'relative', paddingBottom: '133.33%' }}>
          <img
            src={drama.poster && drama.poster.trim() !== "" ? drama.poster : 'https://placehold.co/400x533?text=Drama'}
            alt={drama.title || drama.name}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              cursor: 'pointer'
            }}
            onClick={handleDramaClick}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Chip
              icon={<PlayCircleIcon />}
              label={`${drama.views || drama.viewCount || 0}次播放`}
              size="small"
              color="primary"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.7rem',
                cursor: 'default',
                '& .MuiChip-icon': {
                  color: '#1976d2', // 蓝色
                },
                '& .MuiChip-label': {
                  color: '#1976d2', // 蓝色
                },
              }}
              onClick={(e) => e.stopPropagation()}
              id={`play-count-${drama.id}`}
            />
            <Chip
              icon={<FavoriteIcon />}
              label={`${favoriteCount}次收藏`}
              size="small"
              color="primary"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.7rem',
                cursor: 'pointer',
                '& .MuiChip-icon': {
                  color: isFavoritedState ? '#f50057' : '#1976d2', // 收藏时红色，未收藏时蓝色
                },
                '& .MuiChip-label': {
                  color: isFavoritedState ? '#f50057' : '#1976d2', // 收藏时红色，未收藏时蓝色
                },
              }}
              onClick={handleFavoriteClick}
              id={`favorite-chip-${drama.id}`}
            />
          </Box>
        </Box>

        <Box sx={{ p: isMobile ? 1.5 : 2 }}>
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography 
              variant={isMobile ? 'subtitle1' : 'h6'} 
              component="h3" 
              sx={{ fontSize: isMobile ? '1rem' : '1.25rem'}}
            >
              {drama.title || drama.name}
            </Typography>
            <Box 
              sx={{ display: 'flex', alignItems: 'center', gap: 1}}
              onClick={handleShareClick}
              id={`share-box-${drama.id}`}
            >
              <ShareIcon fontSize={isMobile ? 'small' : 'medium'} />
              <Typography variant="body2" fontSize={isMobile ? '0.875rem' : '0.9rem'}>
                分享
              </Typography>
            </Box>
          </Box>
          <Chip
            label={drama.category || "未分类"}
            size="small"
            color="primary"
            sx={{ mr: 1, mb: 1, fontSize: '0.75rem' }}
            onClick={(e) => e.stopPropagation()}
            id={`category-chip-${drama.id}`}
          />

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, flexGrow: 1, fontSize: isMobile ? '0.875rem' : '0.9rem'}}
          >
            {drama.description && drama.description.length > 95
              ? `${drama.description.slice(0, 95)}……`
              : drama.description}
          </Typography>

          {/* 显示剧集列表 */}
          {drama.episodes && drama.episodes.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ fontSize: '0.875rem', display: 'block', mb: 1 }}
              >
                剧集列表 ({drama.episodes.length}集)
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 1,
                mb: 2
              }}>
                {drama.episodes.slice(0, 3).map((episode, index) => (
                  <Box 
                    key={episode.id} 
                    sx={{ 
                      position: 'relative',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      aspectRatio: '16/9',
                      '&:hover': {
                        opacity: 0.8,
                        transition: 'opacity 0.2s ease-in-out'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('剧集点击:', episode.title);
                      history.push(`/drama/${drama.id}?episode=${index + 1}`);
                    }}
                  >
                    <img
                      src={episode.image_url && episode.image_url.trim() !== "" ? episode.image_url : 'https://placehold.co/160x90?text=Episode'}
                      alt={episode.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {episode.title}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* 分享弹窗 */}
      <ShareDialog
        open={isShareDialogOpen}
        onClose={handleCloseShareDialog}
        dramaTitle={drama.name}
        dramaId={drama.id}
      />

      {/* 登录提示弹窗 */}
      <Dialog
        open={isLoginRequiredDialogOpen}
        onClose={() => setIsLoginRequiredDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FavoriteIcon color="primary" />
            <Typography variant="h6" component="span">
              需要登录
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Typography variant="body1" color="text.primary" gutterBottom>
              登录后才能收藏剧集
            </Typography>
            <Typography variant="body2" color="text.secondary">
              登录后还可以享受更多功能
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setIsLoginRequiredDialogOpen(false)}
            color="inherit"
          >
            取消
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={() => {
              setIsLoginRequiredDialogOpen(false);
              // 跳转到登录页，优先使用 MongoDB 登录系统
              window.location.href = '/login-v2';
            }}
            startIcon={<FavoriteIcon />}
          >
            去登录
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default DramaCard;
